import {setStoredAuthToken} from "shared/utils/authSession";
import {clearClientWorkspaceState} from "shared/utils/clientWorkspaceStorage";

/** React only starts requests after this wrapper is installed in index.tsx. */
export function installReloginFetch(apiUrl: string): void {
    const nativeFetch = window.fetch.bind(window);
    const api = new URL(apiUrl, window.location.href);
    const apiPath = `${api.pathname.replace(/\/+$/, "")}/api/`;
    let redirecting = false;

    const isApiUrl = (value: string) => {
        const url = new URL(value, window.location.href);
        return url.origin === api.origin && url.pathname.startsWith(apiPath);
    };

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        let requestToken: string | null = null;
        try {
            const request = input instanceof Request ? input : null;
            const url = request?.url ?? String(input);
            const headers = new Headers(init?.headers ?? request?.headers);
            const token = localStorage.getItem("authToken");
            if (token && isApiUrl(url) && headers.get("Authorization") === `Bearer ${token}`) {
                requestToken = token;
            }
        } catch {
            // Inspection must not change ordinary fetch validation or behavior.
        }

        // Caller-owned Idempotency-Key identifies an explicit user intent. Preserve
        // it unchanged, including retries; unrelated legacy calls stay unchanged.
        const response = await nativeFetch(input, init);
        if (!requestToken || response.status !== 401 || redirecting) return response;

        try {
            // Do not trust a redirect to a different origin or consume the caller's body.
            if (response.url && !isApiUrl(response.url)) return response;
            const body = await response.clone().json();
            if (body?.code !== "RELOGIN_REQUIRED" || redirecting ||
                localStorage.getItem("authToken") !== requestToken) return response;

            redirecting = true;
            setStoredAuthToken(null);
            clearClientWorkspaceState();
            window.location.replace("/login");
        } catch {
            // Non-JSON errors and unavailable browser storage keep their original response.
        }
        return response;
    };
}
